import React from "react";
import {IconButton, Link, Typography} from "@mui/material";
import {TableChartOutlined} from "@mui/icons-material";

import PageHeader from "../components/Common/PageHeader";
import useBreakpoint from "../util/useBreakpoint";
import {openGuide} from "../util/guideWindow";
import {Blue3, Grey5} from "../theme/styles";
import css_buttons from "../components/Common/css/CCButton.module.scss";

/**
 * Every guide this app ships, in one place.
 *
 * THE SAME PAGE AS t1 AND t5, not a page that resembles them: the card, the breakpoint column
 * counts, the icon button, the rule above "Open guide" are all theirs. Three apps whose
 * documentation index looks like three different products is a worse outcome than one card looking
 * slightly sparse here.
 *
 * ONE CARD TODAY, AND THE PAGE IS STILL WORTH HAVING. The Data Catalog learned this the hard way:
 * its guides were reachable only from the pages they described, so somebody who had not already
 * found a feature could not find its guide either, and nobody could see what documentation existed
 * at all.
 *
 * THEY OPEN IN A WINDOW, not a route: the guides are static HTML in public/docs, written to be read
 * beside the app and printed. Every in-page link to them works the same way.
 */

type Guide = {icon: React.ReactNode; title: string; description: string; file: string};

export default function Documentation() {
    const breakpoint = useBreakpoint();
    const iconSx = {color: Blue3, width: "75px", height: "75px"};

    // Titles and descriptions are written in each guide's own voice, from its <title> and opening
    // line, so this page does not describe a guide differently from how it describes itself.
    //
    // THAT IS NOT SELF-MAINTAINING, and saying it was is how this page went stale. Reference data
    // grew two whole capabilities -- dependent lists and hierarchies -- across four commits to its
    // guide, and this card went on summarizing the guide as it had been, so somebody scanning for
    // "hierarchy" found nothing. A guide that gains a SECTION needs no change here; a guide that
    // gains a CAPABILITY does. guideLinks.test.ts cannot catch it: both halves of the link are
    // still correct, and only the sentence between them is wrong.
    // In the guide's own voice, from its <title> and opening line, so this page does not describe
    // it differently from how it describes itself.
    const guides: Guide[] = [
        {icon: <TableChartOutlined sx={iconSx}/>, title: "Reporting", file: "reporting.html",
         description: "Query your own records — including the answers on your forms — by arranging fields rather than writing SQL. Draw the answer, put it on a dashboard, run it on a schedule, and deliver it where another system can read it."},
    ];

    // THE SAME WINDOW THE APP'S OWN BOOK ICONS USE. This page used to name its windows
    // `cresDocs_<file>`, its own scheme, so opening Reference data here and from Tables gave two
    // windows of one document. See util/guideWindow.
    const open = (guide: Guide) => openGuide(guide.file);

    const renderCard = (guide: Guide, key: number, width: string) => (
        <div key={key} style={{display: "flex", flexDirection: "column", justifyContent: "flex-start",
                               margin: "0 10px 10px 0", border: "1px solid #EDEFF3", borderRadius: "10px",
                               width, padding: "15px", boxSizing: "border-box"}}>
            <div style={{display: "flex", flexDirection: "row"}}>
                <IconButton className={css_buttons.blueIconButton} style={{height: "75px"}} onClick={() => open(guide)}>
                    {guide.icon}
                </IconButton>
                <div style={{marginLeft: "15px", marginTop: "25px"}}>
                    <Link underline="none" variant="inherit" color="inherit" href="#"
                          onClick={(event) => { event.preventDefault(); open(guide); }}>
                        <Typography style={{fontSize: "16px", fontWeight: 400, color: "#000000", textAlign: "left"}}>
                            {guide.title}
                        </Typography>
                    </Link>
                </div>
            </div>
            <div style={{marginTop: "10px", height: "60px"}}>
                <Typography style={{fontSize: "14px", fontWeight: 400, color: Grey5, textAlign: "left"}}>
                    {guide.description}
                </Typography>
            </div>
            <div style={{marginTop: "20px", borderBottom: "1px solid #EDEFF3"}}/>
            <div style={{marginTop: "10px"}}>
                <Link underline="none" variant="inherit" color="inherit" href="#"
                      onClick={(event) => { event.preventDefault(); open(guide); }}>
                    <Typography style={{fontSize: "13px", fontWeight: 600, color: Blue3, marginTop: "10px"}}>Open guide</Typography>
                </Link>
            </div>
        </div>
    );

    const name = breakpoint.getBreakPointName();
    const perRow = name === "xl" ? 4 : name === "lg" ? 3 : name === "md" ? 2 : name === "sm" ? 2 : 1;
    const width = `calc(${100 / perRow}% - 10px)`;
    const sorted = [...guides].sort((a, b) => a.title.localeCompare(b.title));
    const rows: Guide[][] = [];
    for (let i = 0; i < sorted.length; i += perRow) rows.push(sorted.slice(i, i + perRow));

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0, height: "100%", backgroundColor: "#FFFFFF"}}>
            <PageHeader title="Documentation"/>
            <div style={{flex: 1, overflow: "auto", padding: "10px 20px 40px"}}>
                <Typography style={{fontSize: 13, color: Grey5, margin: "14px 0 4px"}}>
                    Every guide this app ships. Each opens in its own window, so you can read it beside what you are doing — and print it.
                </Typography>
                {rows.map((row, rowIndex) => (
                    <div key={rowIndex} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start",
                                                marginTop: rowIndex === 0 ? "16px" : 0}}>
                        {row.map((guide, index) => renderCard(guide, rowIndex * perRow + index, width))}
                    </div>
                ))}
            </div>
        </div>
    );
}
